import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import { adminGetJobMetrics } from '../../lib/adminApi';

const STATUS_COLOR = { complete:'#16a34a', processing:'#0176CE', pending:'#d97706', failed:'#dc2626', awaiting_payment:'#7c3aed' };

const ROUTE_COLOR = { jina:'#0176CE', sonnet_knowledge:'#7c3aed', brave_haiku:'#ea580c', brave_only:'#6b7280' };
const SOURCE_COLOR = { sonnet_synthesis:'#7c3aed', jina:'#0176CE', sonnet_knowledge:'#7c3aed', brave_haiku:'#ea580c', haiku:'#16a34a', skipped_person:'#6b7280', skipped_internal:'#9ca3af', unknown:'#d1d5db' };
const CONF_COLOR = { high:'#16a34a', medium:'#d97706', low:'#dc2626', 'n/a':'#9ca3af' };

function StatCard({ label, value, sub, color }) {
  return (
    <div style={s.card}>
      <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || '#0a2540' }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Bar({ pct, color }) {
  return (
    <div style={{ background: '#e5e7eb', borderRadius: 4, height: 8, flex: 1, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, background: color || '#0176CE', height: '100%', borderRadius: 4, transition: 'width 0.4s ease' }} />
    </div>
  );
}

export default function AdminJobMetrics() {
  const { jobId } = useParams();
  const navigate  = useNavigate();
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminGetJobMetrics(jobId)
      .then(setData)
      .catch(e => setError('Failed to load metrics: ' + (e?.message || e)));
  }, [jobId]);

  if (error) return (
    <div style={s.page}>
      <AdminSidebar />
      <main style={s.main}><div style={{ color: '#dc2626' }}>{error}</div></main>
    </div>
  );

  if (!data) return (
    <div style={s.page}>
      <AdminSidebar />
      <main style={s.main}><div style={{ color: '#6b7280' }}>Loading…</div></main>
    </div>
  );

  const { job, pipeline, final_sources, confidence, domain_coverage, brave_search_count, cost, runtime_minutes } = data;
  const fmtDate = d => d ? new Date(d).toLocaleString() : '—';
  const fmtMin  = m => m >= 60 ? `${Math.floor(m/60)}h ${m%60}m` : `${m}m`;

  return (
    <div style={s.page}>
      <AdminSidebar />
      <main style={s.main}>

        {/* Header */}
        <div style={s.header}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div>
              <button onClick={() => navigate('/admin/jobs')} style={s.backBtn}>← Jobs</button>
              <h1 style={s.title}>Job #{job.job_id} — Enrichment Metrics</h1>
              <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                {job.tenant_name} · {job.file_name} ·{' '}
                <span style={{ color: STATUS_COLOR[job.status] || '#6b7280', fontWeight: 600 }}>{job.status}</span>
              </div>
            </div>
            <button onClick={() => navigate(`/admin/jobs/${job.job_id}/execution`)} style={{ background: '#0176CE', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 24 }}>
              Execution Log →
            </button>
          </div>
        </div>

        {/* Top stat cards */}
        <div style={s.cardRow}>
          <StatCard label="Total Records"   value={job.record_count?.toLocaleString()} />
          <StatCard label="Processed"       value={job.processed_count?.toLocaleString()} sub={`${job.record_count ? Math.round((job.processed_count/job.record_count)*100) : 0}% complete`} />
          <StatCard label="Runtime"         value={fmtMin(runtime_minutes)} sub={`${fmtDate(job.created_on_utc)} → ${fmtDate(job.completed_on_utc)}`} />
          <StatCard label="Domain Coverage" value={`${domain_coverage.pct}%`} sub={`${domain_coverage.with_domain} of ${domain_coverage.total} records`} color={domain_coverage.pct >= 70 ? '#16a34a' : '#d97706'} />
          <StatCard label="Total Cost"      value={cost.amount_usd != null ? `$${cost.amount_usd.toFixed(2)}` : '—'} sub={cost.cost_per_vendor != null ? `$${cost.cost_per_vendor.toFixed(2)} / vendor` : null} />
        </div>

        <div style={s.twoCol}>

          {/* Pipeline Routing */}
          <section style={s.section}>
            <h2 style={s.sectionTitle}>P1 Routing — Enrichment Strategy</h2>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              How Haiku classified each vendor after initial analysis
            </div>
            {pipeline.routes.map(r => (
              <div key={r.route} style={s.barRow}>
                <div style={{ width: 160, fontSize: 13, color: '#374151' }}>{r.label}</div>
                <Bar pct={r.pct} color={ROUTE_COLOR[r.route]} />
                <div style={{ width: 70, textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                  {r.count} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({r.pct}%)</span>
                </div>
              </div>
            ))}
            <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
              Brave searches run: <strong style={{ color: '#374151' }}>{brave_search_count}</strong>
            </div>
          </section>

          {/* Final Source */}
          <section style={s.section}>
            <h2 style={s.sectionTitle}>Final Enrichment Source</h2>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              Which pipeline stage produced the final result
            </div>
            {final_sources.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No results yet</div>}
            {final_sources.map(r => (
              <div key={r.source} style={s.barRow}>
                <div style={{ width: 160, fontSize: 13, color: '#374151' }}>{r.source || 'unknown'}</div>
                <Bar pct={r.pct} color={SOURCE_COLOR[r.source] || '#6b7280'} />
                <div style={{ width: 70, textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                  {r.count} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({r.pct}%)</span>
                </div>
              </div>
            ))}
          </section>

          {/* Confidence Distribution */}
          <section style={s.section}>
            <h2 style={s.sectionTitle}>Confidence Distribution</h2>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
              Quality score of final enrichment result
            </div>
            {confidence.map(r => (
              <div key={r.level} style={s.barRow}>
                <div style={{ width: 80, fontSize: 13, color: '#374151', textTransform: 'capitalize' }}>{r.level}</div>
                <Bar pct={r.pct} color={CONF_COLOR[r.level]} />
                <div style={{ width: 70, textAlign: 'right', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                  {r.count} <span style={{ color: '#9ca3af', fontWeight: 400 }}>({r.pct}%)</span>
                </div>
              </div>
            ))}
          </section>

          {/* Job Details */}
          <section style={s.section}>
            <h2 style={s.sectionTitle}>Job Details</h2>
            {[
              ['Job ID',        `#${job.job_id}`],
              ['Tenant',        job.tenant_name],
              ['User',          job.user_email],
              ['File',          job.file_name],
              ['Process Mode',  job.process_mode || '—'],
              ['New Records',   job.new_record_count ?? '—'],
              ['Started',       fmtDate(job.created_on_utc)],
              ['Completed',     fmtDate(job.completed_on_utc)],
              ['Runtime',       fmtMin(runtime_minutes)],
              ['Status',        job.status],
            ].map(([label, value]) => (
              <div key={label} style={s.detailRow}>
                <span style={s.detailLabel}>{label}</span>
                <span style={s.detailValue}>{value}</span>
              </div>
            ))}
          </section>

        </div>
      </main>
    </div>
  );
}

const s = {
  page:         { display: 'flex', minHeight: '100vh', background: '#f8fafc' },
  main:         { flex: 1, padding: '36px 40px', overflowY: 'auto' },
  header:       { marginBottom: 28 },
  backBtn:      { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 13, padding: '0 0 8px 0', display: 'block' },
  title:        { fontSize: 22, fontWeight: 700, color: '#0a2540', margin: '4px 0 0 0' },
  cardRow:      { display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' },
  card:         { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '18px 22px', minWidth: 150, flex: 1 },
  twoCol:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  section:      { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '20px 24px' },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#0a2540', margin: '0 0 4px 0' },
  barRow:       { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  detailRow:    { display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', padding: '7px 0', fontSize: 13 },
  detailLabel:  { color: '#6b7280' },
  detailValue:  { color: '#0a2540', fontWeight: 500, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' },
};
